import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { contactApi, settingsApi } from '../../services/api';

const C = {
  accent: '#c96428', text1: '#1a0f00', text3: '#6b4c35', text4: '#8b6347',
  border: '#f0e8df', surface: '#fffaf7', greenL: '#f0fdf4', green: '#16a34a',
};

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread' | 'read'
  const [cafeEmail, setCafeEmail] = useState('');
  const [cafeName, setCafeName] = useState('');

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await contactApi.getAll();
      setMessages(res.data.data.messages || []);
      setUnread(res.data.data.unread || 0);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    settingsApi.get().then(r => {
      const d = r.data.data || {};
      setCafeEmail(d.contact_email || '');
      setCafeName(d.name || 'Cafe');
    }).catch(() => {});
  }, []);

  const handleSelect = async (msg) => {
    setSelected(msg);
    if (!msg.is_read) {
      try {
        await contactApi.markRead(msg.id);
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
        setUnread(prev => Math.max(0, prev - 1));
        window.dispatchEvent(new Event('messages-updated'));
      } catch {}
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await contactApi.markAllRead();
      setMessages(prev => prev.map(m => ({ ...m, is_read: true })));
      setUnread(0);
      window.dispatchEvent(new Event('messages-updated'));
      toast.success('All marked as read');
    } catch {
      toast.error('Failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await contactApi.delete(deleteConfirm);
      setMessages(prev => {
        const deleted = prev.find(m => m.id === deleteConfirm);
        if (deleted && !deleted.is_read) setUnread(u => Math.max(0, u - 1));
        return prev.filter(m => m.id !== deleteConfirm);
      });
      if (selected?.id === deleteConfirm) setSelected(null);
      setDeleteConfirm(null);
      toast.success('Message deleted');
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const filtered = messages.filter(m =>
    filter === 'all' ? true : filter === 'unread' ? !m.is_read : m.is_read
  );

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontFamily: '"Playfair Display",serif', fontSize: 24, fontWeight: 700, color: C.text1, margin: 0 }}>Contact Messages</h1>
            {unread > 0 && (
              <span style={{ background: C.accent, color: '#fff', fontSize: 12, fontWeight: 800, borderRadius: 99, padding: '2px 10px', lineHeight: 1.6 }}>
                {unread} new
              </span>
            )}
          </div>
          <p style={{ fontSize: 13, color: C.text4, marginTop: 4 }}>Messages sent by customers from the Contact page</p>
        </div>
        {unread > 0 && (
          <button onClick={handleMarkAllRead}
            style={{ padding: '8px 18px', borderRadius: 10, border: `1.5px solid ${C.border}`, background: '#fff', color: C.text3, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            ✅ Mark All Read
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['all', 'All', messages.length], ['unread', 'Unread', unread], ['read', 'Read', messages.length - unread]].map(([val, label, count]) => (
          <button key={val} onClick={() => setFilter(val)}
            style={{ padding: '7px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
              background: filter === val ? 'linear-gradient(135deg,#c96428,#9a4a1e)' : '#fff',
              color: filter === val ? '#fff' : C.text3,
              border: `1.5px solid ${filter === val ? C.accent : C.border}`,
            }}>
            {label} ({count})
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 240px)', minHeight: 480 }}>

        {/* LEFT: Message list */}
        <div style={{ width: 320, flexShrink: 0, background: '#fff', borderRadius: 18, border: `1px solid ${C.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <div key={i} style={{ height: 72, borderRadius: 12, background: '#f5ede4', marginBottom: 8, animation: 'pulse 1.5s infinite' }} />
              ))
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 16px', color: C.text4 }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>📭</div>
                <p style={{ fontSize: 14, fontWeight: 600, color: C.text3 }}>No messages</p>
              </div>
            ) : (
              filtered.map(msg => (
                <button key={msg.id} onClick={() => handleSelect(msg)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 12, marginBottom: 6,
                    border: `1.5px solid ${selected?.id === msg.id ? C.accent : msg.is_read ? C.border : '#fcd9b0'}`,
                    background: selected?.id === msg.id ? '#fff4ee' : msg.is_read ? '#fff' : '#fffaf5',
                    cursor: 'pointer', transition: 'all 0.15s', fontFamily: 'inherit',
                    boxShadow: selected?.id === msg.id ? '0 2px 10px rgba(201,100,40,0.15)' : 'none',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{msg.name}</span>
                    {!msg.is_read && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.accent, flexShrink: 0 }} />
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: C.text4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3 }}>{msg.email}</div>
                  <div style={{ fontSize: 12, color: C.text3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{msg.message}</div>
                  <div style={{ fontSize: 10, color: '#bbb', marginTop: 4 }}>{fmtDate(msg.created_at)}</div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* RIGHT: Message detail */}
        <div style={{ flex: 1, background: '#fff', borderRadius: 18, border: `1px solid ${C.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.text4 }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>💬</div>
              <p style={{ fontSize: 16, fontWeight: 600, fontFamily: '"Playfair Display",serif', color: C.text3, margin: 0 }}>Select a message</p>
              <p style={{ fontSize: 13, color: C.text4, marginTop: 6 }}>to read the full content</p>
            </div>
          ) : (
            <>
              {/* Detail header */}
              <div style={{ padding: '18px 24px 14px', borderBottom: `1px solid ${C.border}`, background: 'linear-gradient(135deg,#fff8f3,#fff)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 12, background: 'linear-gradient(135deg,#c96428,#9a4a1e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>👤</div>
                    <div>
                      <h2 style={{ fontFamily: '"Playfair Display",serif', fontSize: 17, fontWeight: 700, color: C.text1, margin: 0 }}>{selected.name}</h2>
                      <a href={`mailto:${selected.email}`} style={{ fontSize: 12, color: C.accent, textDecoration: 'none' }}>{selected.email}</a>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#bbb', marginTop: 8 }}>📅 {fmtDate(selected.created_at)}</div>
                </div>
                <button onClick={() => setDeleteConfirm(selected.id)}
                  style={{ width: 36, height: 36, borderRadius: 10, border: '1.5px solid #fee2e2', background: '#fff5f5', color: '#ef4444', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  🗑️
                </button>
              </div>

              {/* Message body */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: '20px 24px', fontSize: 15, color: C.text1, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {selected.message}
                </div>
                <a
                  href={(() => {
                    const to = encodeURIComponent(selected.email);
                    const subject = encodeURIComponent(`Re: Your message to ${cafeName}`);
                    const body = encodeURIComponent(
                      `Hi ${selected.name},\n\nThank you for reaching out to us!\n\n` +
                      `We received your message:\n"${selected.message}"\n\n` +
                      `—\n${cafeName}${cafeEmail ? `\n${cafeEmail}` : ''}`
                    );
                    const cc = cafeEmail ? `&cc=${encodeURIComponent(cafeEmail)}` : '';
                    // Gmail compose URL — opens directly in browser, no mail app needed
                    return `https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}${cc ? `&cc=${encodeURIComponent(cafeEmail)}` : ''}&body=${body}`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 20, padding: '12px 24px', borderRadius: 12, background: 'linear-gradient(135deg,#c96428,#9a4a1e)', color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none', boxShadow: '0 4px 14px rgba(201,100,40,0.35)' }}>
                  ✉️ Reply via Gmail
                </a>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete confirm */}
      <AnimatePresence>
        {deleteConfirm && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              style={{ position: 'absolute', inset: 0, background: 'rgba(26,15,0,0.7)', backdropFilter: 'blur(4px)' }}
              onClick={() => setDeleteConfirm(null)} />
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
              style={{ position: 'relative', background: '#1a0f00', border: '1px solid #3d2010', borderRadius: 22, padding: 32, width: '100%', maxWidth: 360, textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.4)' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🗑️</div>
              <h3 style={{ fontFamily: '"Playfair Display",serif', fontSize: 18, fontWeight: 700, color: '#fde0cc', margin: '0 0 8px' }}>Delete Message?</h3>
              <p style={{ fontSize: 13, color: '#8b6347', marginBottom: 24 }}>This action cannot be undone.</p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button onClick={() => setDeleteConfirm(null)}
                  style={{ flex: 1, padding: 11, borderRadius: 12, border: '1px solid #3d2010', background: 'transparent', color: '#a07858', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Cancel
                </button>
                <button onClick={handleDelete} disabled={deleting}
                  style={{ flex: 1, padding: 11, borderRadius: 12, border: 'none', background: deleting ? '#888' : '#ef4444', color: '#fff', fontSize: 14, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.55}}`}</style>
    </div>
  );
};

export default AdminMessages;
